import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Settings, User, Building2, Bell, Shield, Palette, Globe,
  Save, RefreshCw, Mail, Phone, MapPin, Clock, Key, Camera, Wrench, UserCheck, AlertTriangle, CheckCircle2, Loader2
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CenterSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [syncAuthLoading, setSyncAuthLoading] = useState(false);
  const [syncAuthConfirmOpen, setSyncAuthConfirmOpen] = useState(false);
  const [syncAuthResult, setSyncAuthResult] = useState(null);
  const [repairInstLoading, setRepairInstLoading] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(0);
  
  const [profile, setProfile] = useState({
    center_name: '',
    owner_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    center_code: '',
    opening_time: '09:00',
    closing_time: '18:00',
    description: '',
    logo_url: ''
  });

  const [notifications, setNotifications] = useState({
    email_notifications: true,
    sms_notifications: true,
    fee_reminders: true,
    attendance_alerts: true,
    new_lead_alerts: true
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const runSyncAuth = async () => {
    // Rate limit: 1 request / 30 sec
    const now = Date.now();
    if (now - lastSyncAt < 30000) {
      toast.error('Please wait 30 seconds between sync runs');
      return;
    }
    setSyncAuthConfirmOpen(false);
    setSyncAuthLoading(true);
    setLastSyncAt(now);
    try {
      const res = await axios.post(`${API}/center/backfill-student-auth`, {}, { headers: getAuthHeaders() });
      setSyncAuthResult(res.data);
      toast.success('Sync complete', {
        description: `${res.data.users_created} new • ${res.data.users_linked} linked • ${res.data.sms_sent} SMS sent`
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncAuthLoading(false);
    }
  };

  const runRepairInstallments = async () => {
    setRepairInstLoading(true);
    try {
      const res = await axios.post(`${API}/center/repair-installment-status`, {}, { headers: getAuthHeaders() });
      toast.success(`Installments repaired: ${res.data.students_repaired}/${res.data.total_scanned} students fixed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairInstLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/center/me`, { headers: getAuthHeaders() });
      if (response.data) {
        setProfile({
          center_name: response.data.center_name || '',
          owner_name: response.data.owner_name || '',
          email: response.data.email || '',
          phone: response.data.mobile || '',
          address: response.data.address || '',
          city: response.data.district || '',
          state: response.data.state || '',
          pincode: response.data.pincode || '',
          center_code: response.data.center_code || '',
          opening_time: '09:00',
          closing_time: '18:00',
          description: '',
          logo_url: response.data.logo_url || ''
        });
      }
    } catch (error) {
      // Default empty profile if API fails
      console.error('Fetch profile error:', error);
      setProfile({
        center_name: '',
        owner_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        center_code: '',
        opening_time: '09:00',
        closing_time: '18:00',
        description: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/center/profile`, profile, { headers: getAuthHeaders() });
      toast.success('Profile updated successfully / प्रोफ़ाइल अपडेट हो गई');
    } catch (error) {
      console.error('Save profile error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const saveNotifications = async () => {
    setIsSaving(true);
    try {
      toast.success('Notification settings saved');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Center Profile', icon: Building2 },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500 mt-1">Manage your center preferences</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${
                  activeTab === tab.id 
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-600' 
                    : 'text-slate-600 hover:bg-slate-50 border-l-4 border-transparent'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-500" />
                Center Profile
              </h3>
              
              {/* Center Code Badge */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6 border border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-500 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold">IQ</span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Center Code</p>
                    <p className="text-lg font-mono font-bold text-purple-700">{profile.center_code}</p>
                  </div>
                </div>
              </div>
              
              {/* Center Logo upload — applied to ID Cards & Certificates in real time */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-white border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {profile.logo_url ? (
                      <img src={profile.logo_url} alt="Center logo" className="w-full h-full object-contain" />
                    ) : (
                      <Building2 className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">Center Logo</p>
                    <p className="text-xs text-slate-500 mt-0.5">Used on Staff ID cards & certificates. PNG/JPG, max ~500 KB.</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        id="center-logo-input"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 500 * 1024) {
                            toast.error('Logo must be ≤ 500 KB');
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = (ev) => setProfile({ ...profile, logo_url: ev.target.result });
                          reader.readAsDataURL(f);
                        }}
                        data-testid="center-logo-upload"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => document.getElementById('center-logo-input').click()}
                      >
                        {profile.logo_url ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                      {profile.logo_url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setProfile({ ...profile, logo_url: '' })}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Center Name</Label>
                    <Input
                      value={profile.center_name}
                      onChange={(e) => setProfile({...profile, center_name: e.target.value})}
                      placeholder="Your center name"
                    />
                  </div>
                  <div>
                    <Label>Owner Name</Label>
                    <Input
                      value={profile.owner_name}
                      onChange={(e) => setProfile({...profile, owner_name: e.target.value})}
                      placeholder="Owner full name"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({...profile, email: e.target.value})}
                        className="pl-10"
                        placeholder="center@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={profile.phone}
                        onChange={(e) => setProfile({...profile, phone: e.target.value})}
                        className="pl-10"
                        placeholder="9876543210"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label>Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      value={profile.address}
                      onChange={(e) => setProfile({...profile, address: e.target.value})}
                      className="pl-10"
                      placeholder="Full address"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={profile.city}
                      onChange={(e) => setProfile({...profile, city: e.target.value})}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={profile.state}
                      onChange={(e) => setProfile({...profile, state: e.target.value})}
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <Label>Pincode</Label>
                    <Input
                      value={profile.pincode}
                      onChange={(e) => setProfile({...profile, pincode: e.target.value})}
                      placeholder="302001"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Opening Time</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="time"
                        value={profile.opening_time}
                        onChange={(e) => setProfile({...profile, opening_time: e.target.value})}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Closing Time</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="time"
                        value={profile.closing_time}
                        onChange={(e) => setProfile({...profile, closing_time: e.target.value})}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="pt-4">
                  <Button 
                    onClick={saveProfile} 
                    disabled={isSaving}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Bell className="w-5 h-5 text-purple-500" />
                Notification Preferences
              </h3>
              
              <div className="space-y-4">
                {[
                  { key: 'email_notifications', label: 'Email Notifications', desc: 'Receive updates via email' },
                  { key: 'sms_notifications', label: 'SMS Notifications', desc: 'Receive updates via SMS' },
                  { key: 'fee_reminders', label: 'Fee Reminders', desc: 'Get alerts for pending fees' },
                  { key: 'attendance_alerts', label: 'Attendance Alerts', desc: 'Low attendance notifications' },
                  { key: 'new_lead_alerts', label: 'New Lead Alerts', desc: 'Get notified for new leads' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div>
                      <p className="font-medium text-slate-900">{item.label}</p>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                    <button
                      onClick={() => setNotifications({...notifications, [item.key]: !notifications[item.key]})}
                      className={`w-12 h-6 rounded-full transition-all ${
                        notifications[item.key] ? 'bg-purple-600' : 'bg-slate-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        notifications[item.key] ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                ))}
                
                <div className="pt-4">
                  <Button 
                    onClick={saveNotifications}
                    disabled={isSaving}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Preferences
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-500" />
                Security Settings
              </h3>
              
              <div className="space-y-6">
                {/* Change Password */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Change Password
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <Label>Current Password</Label>
                      <Input type="password" placeholder="Enter current password" />
                    </div>
                    <div>
                      <Label>New Password</Label>
                      <Input type="password" placeholder="Enter new password" />
                    </div>
                    <div>
                      <Label>Confirm New Password</Label>
                      <Input type="password" placeholder="Confirm new password" />
                    </div>
                    <Button className="bg-gradient-to-r from-purple-600 to-blue-600">
                      Update Password
                    </Button>
                  </div>
                </div>
                
                {/* Two Factor Auth */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900">Two-Factor Authentication</h4>
                      <p className="text-sm text-slate-500">Add an extra layer of security</p>
                    </div>
                    <Button variant="outline">Enable 2FA</Button>
                  </div>
                </div>
                
                {/* Recovery Tools (Sync Auth + Repair Installments) */}
                <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200" data-testid="recovery-tools-section">
                  <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-amber-600" /> Recovery Tools
                  </h4>
                  <p className="text-xs text-slate-600 mb-4">Fix login issues for legacy students and repair data inconsistencies</p>
                  
                  <div className="grid md:grid-cols-2 gap-3">
                    {/* Sync Auth Card */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:shadow-md transition-all">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <UserCheck className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-semibold text-slate-900 text-sm">Sync Auth</h5>
                          <p className="text-[11px] text-slate-500 mt-0.5">Create login accounts for students who can't login</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setSyncAuthConfirmOpen(true)}
                        disabled={syncAuthLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm h-9"
                        data-testid="sync-auth-btn"
                      >
                        {syncAuthLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Run Sync</>}
                      </Button>
                      
                      {syncAuthResult && (
                        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="sync-auth-result">
                          <p className="text-xs font-bold text-emerald-700 flex items-center gap-1 mb-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Sync Completed
                          </p>
                          <ul className="text-[11px] text-slate-700 space-y-0.5">
                            <li>✔ {syncAuthResult.users_skipped} students already had login</li>
                            <li>✔ {syncAuthResult.users_created} new accounts created</li>
                            <li>✔ {syncAuthResult.users_linked} linked to existing users</li>
                            <li>✔ SMS sent to {syncAuthResult.sms_sent} students</li>
                            <li className="text-slate-400">Total scanned: {syncAuthResult.total_scanned}</li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Repair Installments Card */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-emerald-300 hover:shadow-md transition-all">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-semibold text-slate-900 text-sm">Repair Installments</h5>
                          <p className="text-[11px] text-slate-500 mt-0.5">Recompute installment status for all students</p>
                        </div>
                      </div>
                      <Button
                        onClick={runRepairInstallments}
                        disabled={repairInstLoading}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm h-9"
                        data-testid="repair-installments-btn"
                      >
                        {repairInstLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Repairing...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Run Repair</>}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sync Auth Confirmation Dialog */}
                <AlertDialog open={syncAuthConfirmOpen} onOpenChange={setSyncAuthConfirmOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        Confirm Sync Auth
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-600">
                        Are you sure? This will scan all your students and create login accounts for those who don't have one. Existing accounts will not be overwritten. SMS will be sent to newly created accounts only.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="cancel-sync-auth">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={runSyncAuth} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-sync-auth">
                        Yes, Run Sync
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {/* Active Sessions */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-medium text-slate-900 mb-4">Active Sessions</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Globe className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Current Session</p>
                          <p className="text-xs text-slate-500">Chrome on Windows • Active now</p>
                        </div>
                      </div>
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CenterSettingsPage;
